var __assign = (this && this.__assign) || function () {
    __assign = Object.assign || function(t) {
        for (var s, i = 1, n = arguments.length; i < n; i++) {
            s = arguments[i];
            for (var p in s) if (Object.prototype.hasOwnProperty.call(s, p))
                t[p] = s[p];
        }
        return t;
    };
    return __assign.apply(this, arguments);
};
var __rest = (this && this.__rest) || function (s, e) {
    var t = {};
    for (var p in s) if (Object.prototype.hasOwnProperty.call(s, p) && e.indexOf(p) < 0)
        t[p] = s[p];
    if (s != null && typeof Object.getOwnPropertySymbols === "function")
        for (var i = 0, p = Object.getOwnPropertySymbols(s); i < p.length; i++) {
            if (e.indexOf(p[i]) < 0 && Object.prototype.propertyIsEnumerable.call(s, p[i]))
                t[p[i]] = s[p[i]];
        }
    return t;
};
import React, { memo, useCallback, useEffect, useState } from 'react';
import cc from 'classcat';
import moment from 'moment';
import CalendarButton from './CalendarButton';
import CalendarWrapper from './CalendarWrapper';
import 'react-dates/initialize';
import ArrowBackIcon from '../../assets/icons/arrow-back.svg';
import ArrowForwardIcon from '../../assets/icons/arrow-forward.svg';
import './calendar.style.scss';
/**
 * A calendar component
 */
var Calendar = function (props) {
    var date = props.date, initialVisibleMonth = props.initialVisibleMonth, isFocused = props.isFocused, onDateChange = props.onDateChange, className = props.className, rest = __rest(props, ["date", "initialVisibleMonth", "isFocused", "onDateChange", "className"]);
    var calendarToggleClass = 'tfm-calendar-toggle';
    var _a = useState(false), showCalendar = _a[0], setShowCalendar = _a[1];
    var _b = useState(null), currentMonthDate = _b[0], setCurrentMonthDate = _b[1];
    var closeCalendar = useCallback(function () { return setShowCalendar(false); }, [setShowCalendar]);
    // This use effect is necessary to let the animation of the calendar go smooth,
    // by setting the correct translate value depending on if the user navigates back or forth.
    // It uses showCalendar with a timeout to make sure to get the elements when they are in the DOM.
    // We do it here in Javascript, since in pure CSS it is not detectable if the user clicked back or forth.
    useEffect(function () {
        if (showCalendar) {
            setTimeout(function () {
                var calendarGridEl = document.getElementsByClassName('CalendarMonthGrid')[0];
                var prevNavButtonEl = document.getElementsByClassName('month-navigation-prev')[0];
                var nextNavButtonEl = document.getElementsByClassName('month-navigation-next')[0];
                prevNavButtonEl === null || prevNavButtonEl === void 0 ? void 0 : prevNavButtonEl.addEventListener('mousedown', function () { return calendarGridEl.style.setProperty('--month-grid-transform', '1'); });
                nextNavButtonEl === null || nextNavButtonEl === void 0 ? void 0 : nextNavButtonEl.addEventListener('mousedown', function () { return calendarGridEl.style.setProperty('--month-grid-transform', '-0.95'); });
            }, 100);
        }
    }, [showCalendar]);
    // Outside click does nothing if clicked on calendar button, to prevent setShowCalendar from toggling twice
    var onOutsideClick = useCallback(function (event) {
        var _a, _b;
        if ((_b = (_a = event.currentTarget.activeElement) === null || _a === void 0 ? void 0 : _a.className) === null || _b === void 0 ? void 0 : _b.includes(calendarToggleClass)) {
            return;
        }
        closeCalendar();
    }, [closeCalendar]);
    // Calls the passed onDateChange callback and closes the calendar
    var dateChanged = useCallback(function (newDate) {
        onDateChange(newDate);
        closeCalendar();
    }, [onDateChange, closeCalendar]);
    var onMonthClick = useCallback(function (dateForMonth) { return setCurrentMonthDate(dateForMonth); }, [setCurrentMonthDate]);
    var navPrevBtn = useCallback(function (_a) {
        var disabled = _a.disabled, onClick = _a.onClick;
        var limit = moment('201301').endOf('month');
        var beforeAcceptedRange = currentMonthDate ? currentMonthDate.isSameOrBefore(limit) : date === null || date === void 0 ? void 0 : date.isSameOrBefore(limit);
        return (React.createElement("button", { className: "month-navigation month-navigation-prev", "aria-label": "\u03A3\u03C4\u03BF\u03BD \u03C0\u03C1\u03BF\u03B7\u03B3\u03BF\u03CD\u03BC\u03B5\u03BD\u03BF \u03BC\u03AE\u03BD\u03B1", type: "button", onClick: onClick, disabled: disabled || beforeAcceptedRange },
            React.createElement(ArrowBackIcon, null)));
    }, [currentMonthDate, date]);
    var navNextBtn = useCallback(function (_a) {
        var disabled = _a.disabled, onClick = _a.onClick;
        var limit = moment().startOf('month');
        var afterAcceptedRange = currentMonthDate ? currentMonthDate.isSameOrAfter(limit) : date === null || date === void 0 ? void 0 : date.isSameOrAfter(limit);
        return (React.createElement("button", { className: "month-navigation month-navigation-next", "aria-label": "\u03A3\u03C4\u03BF\u03BD \u03B5\u03C0\u03CC\u03BC\u03B5\u03BD\u03BF \u03BC\u03AE\u03BD\u03B1", type: "button", onClick: onClick, disabled: disabled || afterAcceptedRange },
            React.createElement(ArrowForwardIcon, null)));
    }, [currentMonthDate, date]);
    return (React.createElement("div", __assign({ className: cc(['tfm-calendar', className]) }, rest),
        React.createElement(CalendarButton, { onButtonClick: function () { return setShowCalendar(!showCalendar); }, className: calendarToggleClass }),
        showCalendar && (React.createElement(CalendarWrapper
        // Since the below 3 properties are optional, but the DayPicker doesn't allow them to be undefined,
        // we make sure here that they're either null or boolean (setting defaultProps doesn't do the trick).
        , { 
            // Since the below 3 properties are optional, but the DayPicker doesn't allow them to be undefined,
            // we make sure here that they're either null or boolean (setting defaultProps doesn't do the trick).
            date: date || null, initialVisibleMonth: initialVisibleMonth || null, isFocused: !!isFocused, onDateChange: dateChanged, onFocusChange: function () { }, onClose: closeCalendar, onOutsideClick: onOutsideClick, onPrevMonthClick: onMonthClick, onNextMonthClick: onMonthClick, navPrevBtn: navPrevBtn, navNextBtn: navNextBtn }))));
};
Calendar.defaultProps = {
    date: moment(),
    initialVisibleMonth: function () { return moment(); },
    isFocused: true,
};
export default memo(Calendar);
